import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin } = FiIcons;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const quickLinks = [
    { name: 'Home', href: '#/' },
    { name: 'Services', href: '#/services' },
    { name: 'Service Areas', href: '#/service-areas' },
    { name: 'About', href: '#/about' },
    { name: 'Contact', href: '#/contact' }
  ];

  const services = [
    'Property Management',
    'Tenant Placement',
    'Maintenance Services',
    'Financial Reporting',
    'Real Estate Investment',
    'Legal Compliance'
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <h3 className="text-2xl font-bold">
              <span className="text-blue-400">DGM</span> Property Management
            </h3>
            <p className="text-gray-400 leading-relaxed">
              Calgary's premier property management company, providing comprehensive services to maximize your real estate investment returns.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-gray-400">
                <SafeIcon icon={FiPhone} className="w-4 h-4" />
                <a href="tel:587-800-1215" className="hover:text-blue-400 transition-colors">
                  587-800-1215
                </a>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                <span>Calgary, Airdrie & Edmonton, Alberta</span>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-gray-400 hover:text-blue-400 transition-colors">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white">Our Services</h4>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service} className="text-gray-400 text-sm">
                  {service}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-white">Get Started Today</h4>
            <p className="text-gray-400 text-sm">
              Ready to maximize your property investment? Contact us for a free consultation.
            </p>
            <motion.a
              href="#/contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
            >
              Free Consultation
            </motion.a>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="border-t border-gray-800 mt-12 pt-8 text-center"
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {currentYear} DGM Property Management. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm">
              Professional Property Management Services in Calgary, Airdrie & Edmonton, Alberta
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;